import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useAlert } from "react-alert";
import { doc, runTransaction } from "firebase/firestore";
import {
  vatValidation,
  priceValidation,
  normalizeOptionalNumber,
} from "../../helper/regexValidations";

import PictureDisplaySelector from "./pictureDisplaySelector/PictureDisplaySelector";
import { db } from "../../firebase";
import { uploadMedia } from "../../utility/uploadMedia";
import deleteImage from "../../utility/deleteImage";
import Input from "../Input";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ReactComponent as AngleLeft } from "../../images/AngleLeft.svg";
import { Icons } from "../../images/icons";
import { CategoriesArray } from "../../utility/data";
import "./AddProduct.scss";
import PosIconModal from "./PosIconModal";
import OutlinedPinkButton from "../buttons/outlinedPinkButton/OutlinedPinkButton";
import { getStockItems, getStockCategories } from "../../utility/getStock";
import StockConfigFields from "./StockConfigFields";
import { generateProductIcon, previewProductIcon } from "../../utility/aiApi";
import { normalizeStockLinks } from "../../utility/stockManagement";

const AddOptionProduct = ({ org, selectedProduct, setAddNew, optionCategories, handleDelete }) => {
  const { t } = useTranslation("editproduct");
  const userStore = useSelector((state) => state.userReducer);
  const orgId = org?.id;

  const [selectedCategory, setSelectedCategory] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);

  const [loader, setLoader] = useState(false);
  const [outOfStock, setOutOfStock] = useState(false);
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [vat, setVat] = useState("");
  const [vatOut, setVatOut] = useState("");
  const [customIcon, setCustomIcon] = useState("");
  const [imageUrl, setImageUrl] = useState();
  const [generatingAiIcon, setGeneratingAiIcon] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [stockCategories, setStockCategories] = useState([]);
  const [stockItemId, setStockItemId] = useState("");
  const [quantityPerSale, setQuantityPerSale] = useState("");
  const [stockLinks, setStockLinks] = useState([]);
  const [outOfStockBehavior, setOutOfStockBehavior] = useState("");

  const fetchStockItems = useCallback(
    async (signal) => {
      try {
        const stockItems = await getStockItems(org?.uid);
        if (!signal.aborted) setStockItems(stockItems);
      } catch (error) {
        console.error(error);
        alert.show(t("stock_items_load_error"), { type: "error" });
      }
    },
    [org?.uid],
  );

  const fetchStockCategories = useCallback(
    async (signal) => {
      try {
        const categories = await getStockCategories(org?.uid);
        if (!signal.aborted) setStockCategories(categories);
      } catch (error) {
        console.error(error);
        alert.show(t("stock_categories_load_error"), { type: "error" });
      }
    },
    [org?.uid],
  );

  function handlePictureChange(event) {
    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setImageUrl(url);
      } else {
        alert.show(t("fileNotImage"));
      }
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    setSelectedCategory(selectedProduct?.category || optionCategories[0]?.title || "");
    setOutOfStock(selectedProduct?.outOfStock || false);
    setTitle(selectedProduct?.title || "");
    setPrice(selectedProduct?.price ?? "");
    setVat(selectedProduct?.vat ?? "");
    setVatOut(selectedProduct?.vatOut ?? "");
    setCustomIcon(selectedProduct?.customIcon || optionCategories[0]?.customIcon || "");
    setImageUrl(selectedProduct?.imageUrl);
    const initialStockLinks = normalizeStockLinks(selectedProduct).map((link) => ({
      stockItemId: link.stockItemId,
      quantityPerSale: `${link.quantityPerSale}`,
    }));
    setStockLinks(initialStockLinks);
    setStockItemId(initialStockLinks[0]?.stockItemId || "");
    if (initialStockLinks.length > 0) {
      setQuantityPerSale(initialStockLinks[0]?.quantityPerSale || "");
      setOutOfStockBehavior(selectedProduct?.outOfStockBehavior || "alert");
    } else {
      setQuantityPerSale("");
      setOutOfStockBehavior("");
    }

    fetchStockItems(abortController.signal);
    fetchStockCategories(abortController.signal);

    return () => abortController.abort();
  }, [selectedProduct, optionCategories, fetchStockItems, fetchStockCategories]);

  const alert = useAlert();

  const saveProduct = async () => {
    const sanitizedStockLinks = stockLinks
      .map((link) => ({
        stockItemId: link?.stockItemId || "",
        quantityPerSale: Number(`${link?.quantityPerSale || ""}`.replace(/,/g, ".")),
      }))
      .filter((link) => link.stockItemId || link.quantityPerSale);
    const invalidStockLink = sanitizedStockLinks.find(
      (link) =>
        !link.stockItemId || !Number.isFinite(link.quantityPerSale) || link.quantityPerSale <= 0,
    );
    const validStockLinks = sanitizedStockLinks.filter(
      (link) => link.stockItemId && Number.isFinite(link.quantityPerSale) && link.quantityPerSale > 0,
    );
    const primaryStockLink = validStockLinks[0];

    if (title === "") {
      alert.show(t("choose_name"), { type: "warning" });
    } else if (price === "") {
      alert.show(t("price_required"), { type: "warning" });
    } else if (!priceValidation(price)) {
      alert.show(t("price_invalid_format"), { type: "warning" });
    } else if (!vatValidation(vat) || !vatValidation(vatOut)) {
      alert.show(t("vat_range"), { type: "warning" });
    } else if (selectedCategory == null) {
      alert.show("Choisissez une catégorie!", { type: "warning" });
    } else if (invalidStockLink) {
      alert.show(t("quantity_per_sale_required"), { type: "warning" });
    } else {
      setLoader(true);
      const id = selectedProduct?.id ? selectedProduct.id : new Date().getTime().toString();
      const productData = {
        title,
        price: normalizeOptionalNumber(price),
        vat: normalizeOptionalNumber(vat),
        vatOut: normalizeOptionalNumber(vatOut),
        customIcon,
        category: selectedCategory,
        id,
        uid: userStore?.users.uid,
        orgId,
        outOfStock,
        stockLinks: validStockLinks,
        stockItemId: primaryStockLink?.stockItemId || null,
        quantityPerSale: primaryStockLink?.quantityPerSale || null,
        outOfStockBehavior: validStockLinks.length > 0 ? outOfStockBehavior || "alert" : null,
      };

      let oldImageUrl = selectedProduct?.imageUrl || null;
      let uploadedImageUrl = null;

      try {
        if (imageUrl && (!selectedProduct || imageUrl !== oldImageUrl)) {
          if (oldImageUrl) {
            await deleteImage(oldImageUrl);
          }
          uploadedImageUrl = await uploadMedia(imageUrl, `OptionProducts/${id}`, false);
          productData.imageUrl = uploadedImageUrl;
        }

        await runTransaction(db, async (transaction) => {
          const productRef = doc(db, "OptionProducts", id);
          transaction.set(productRef, productData, { merge: true });
        });

        setLoader(false);
        setAddNew(false);
        alert.show("Produit enregistré avec succès.", { type: "success" });
      } catch (error) {
        console.log("Error writing document: ", error);
        setLoader(false);
        alert.show("Quelque chose n'a pas marché.", { type: "error" });
        if (uploadedImageUrl) {
          await deleteImage(uploadedImageUrl);
        }
      }
    }
  };

  const handleSelectCustomCategory = (ctg) => {
    setSelectedCategory(ctg.title);
    setCustomIcon(ctg.customIcon);
    setVat(ctg?.vatIn);
    setVatOut(ctg?.vatOut);
  };

  const handleGenerateAiIcon = async () => {
    if (!title?.trim() || !orgId || generatingAiIcon) return;
    setGeneratingAiIcon(true);
    try {
      if (selectedProduct?.id) {
        await generateProductIcon({
          orgId,
          productId: selectedProduct.id,
          collectionName: "OptionProducts",
        });
      } else {
        const result = await previewProductIcon({
          orgId,
          title: title.trim(),
          category: selectedCategory || "Other",
        });
        setImageUrl(result.imageUrl);
      }
      alert.show(t("ai_icon_generation_started"), { type: "success" });
    } catch (error) {
      alert.show(error.message || t("ai_icon_generation_error"), { type: "error" });
    } finally {
      setGeneratingAiIcon(false);
    }
  };

  return (
    <div className="add-product">
      <h4 className="add-product-title" onClick={() => setAddNew(false)}>
        <AngleLeft /> <div className="w-0 flex-grow-1">{t("return")}</div>
      </h4>
      <div className="item">
        <h5 className="subtitle">{t("the_option_is")}</h5>
        <div className="mb-28 gap-12px d-flex">
          <button
            type="button"
            className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
              outOfStock ? "btn-outline-success" : "btn-success active"
            }`}
            onClick={() => setOutOfStock(false)}
          >
            {t("in_stock")}
          </button>
          <button
            type="button"
            className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
              outOfStock ? "btn-danger active" : "btn-outline-danger"
            }`}
            onClick={() => setOutOfStock(true)}
          >
            {t("out_of_stock")}
          </button>
        </div>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="mb-20">
            <Input
              label={t("title")}
              placeholder={t("product_name")}
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required="required"
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("price")}
              placeholder="5.99"
              id="price"
              type="text"
              value={price}
              onChange={(e) => {
                if (priceValidation(e.target.value)) setPrice(e.target.value);
              }}
              required="required"
            />
          </div>
          <div className="mb-20">
            <label className="form-label __form-label">{t("option_categories")}</label>
            <div className="filterRow">
              {optionCategories.map((cat) => (
                <div
                  key={cat.id || cat.title}
                  className={selectedCategory === cat.title ? "filBtn active" : "filBtn"}
                  onClick={() => handleSelectCustomCategory(cat)}
                >
                  <img
                    src={CategoriesArray.find((i) => i.name === cat.customIcon)?.icon}
                    alt={cat?.customIcon}
                    style={{
                      width: 20,
                      height: 20,
                      filter: selectedCategory === cat.title ? "invert(0) brightness(2)" : "none",
                      transition: "filter 0.3s ease",
                      marginRight: 6,
                    }}
                  />
                  <span>{cat.title}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-20">
            <Input
              label={t("optional") + " " + t("vat_in")}
              placeholder="6"
              id="vat"
              type="text"
              value={vat}
              onChange={(e) => {
                if (vatValidation(e.target.value)) setVat(e.target.value);
              }}
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("optional") + " " + t("vat_out")}
              placeholder="21"
              id="vatOut"
              type="text"
              value={vatOut}
              onChange={(e) => {
                if (vatValidation(e.target.value)) setVatOut(e.target.value);
              }}
            />
          </div>
          <StockConfigFields
            stockItemId={stockItemId}
            stockLinks={stockLinks}
            stockItems={stockItems}
            stockCategories={stockCategories}
            quantityPerSale={quantityPerSale}
            outOfStockBehavior={outOfStockBehavior}
            onStockLinksChange={setStockLinks}
            onStockIdChange={setStockItemId}
            onQuantityChange={setQuantityPerSale}
            onOutOfStockBehaviorChange={setOutOfStockBehavior}
          />

          <label className="form-label __form-label">{t("choose_icon") + t("optional")}</label>
          <div className="mb-4">
            <div
              className="productCard max-w-200 mx-auto cursor-pointer"
              onClick={() => setEditModalOpen(true)}
            >
              <span className="color"></span>
              <div className="productImg">
                <img
                  src={
                    customIcon
                      ? CategoriesArray.find((i) => i.name === customIcon)?.icon
                      : Icons.others
                  }
                  alt=""
                />
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="form-label __form-label">{t("choose_image") + t("optional")}</label>
            <PictureDisplaySelector
              onImageChange={(event) => {
                handlePictureChange(event);
              }}
              imageUrl={imageUrl}
              error={null}
              onGenerateAiIcon={handleGenerateAiIcon}
              aiIconLoading={generatingAiIcon}
              aiIconDisabled={!title?.trim()}
              aiIconStatus={
                selectedProduct?.aiIconStatus === "pending"
                  ? t("ai_icon_pending")
                  : selectedProduct?.aiIconStatus === "failed"
                    ? t("ai_icon_failed")
                    : ""
              }
            />
          </div>
          {loader ? (
            <LoadingSpinner height="30%" width="30%" />
          ) : (
            <div className="buttonsWrapper">
              <button
                type="submit"
                className="themeBtn m-0 w-100 pos-submit-btn"
                onClick={saveProduct}
              >
                {t("save")}
              </button>
              {!!selectedProduct && (
                <OutlinedPinkButton onPressFunction={handleDelete}>
                  {t("delete")}
                </OutlinedPinkButton>
              )}
            </div>
          )}
        </form>
        <PosIconModal
          modalOpen={editModalOpen}
          setModalOpen={setEditModalOpen}
          customIcon={customIcon}
          setCustomIcon={setCustomIcon}
          selectedCategory={selectedCategory}
        />
      </div>
    </div>
  );
};

export default AddOptionProduct;
